import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Menu, X } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import BrandMark from "@/components/BrandMark";

export default function PublicHeader() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const loc = useLocation();
  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/services", label: t("nav.services") },
    { to: "/packages", label: t("nav.packages") },
    { to: "/contact", label: t("nav.contact") },
  ];
  return (
    <header
      data-testid="public-header"
      className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-border"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5 group">
          <BrandMark size={38} className="shrink-0 transition-transform group-hover:-rotate-3 duration-300" />
          <div className="leading-none">
            <div className="font-heading font-black tracking-tight text-secondary text-[15px] md:text-base">
              Safeway<span className="text-primary">·</span>Finland
            </div>
            <div className="mt-1.5 text-[9px] font-heading font-black uppercase tracking-[0.22em] text-muted-foreground whitespace-nowrap">
              {t("brand.tagline")}
            </div>
          </div>
        </Link>
        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={`text-sm font-medium hover:text-primary transition-colors ${loc.pathname === l.to ? "text-primary" : "text-secondary"}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-2">
          <LanguageSwitcher />
          {user ? (
            <Link to="/dashboard"><Button data-testid="header-dashboard-btn" variant="default">{t("nav.openPortal")}</Button></Link>
          ) : (
            <>
              <Link to="/login"><Button data-testid="header-login-btn" variant="ghost">{t("nav.signIn")}</Button></Link>
              <Link to="/register"><Button data-testid="header-register-btn">{t("nav.getStarted")}</Button></Link>
            </>
          )}
        </div>
        <div className="md:hidden flex items-center gap-1">
          <LanguageSwitcher />
          <button data-testid="mobile-menu-toggle" className="p-2" onClick={() => setOpen((v) => !v)} aria-label="Toggle menu">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="md:hidden border-t border-border bg-white">
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="text-secondary text-sm font-medium">{l.label}</Link>
            ))}
            {user ? (
              <Link to="/dashboard"><Button className="w-full" data-testid="mobile-dashboard-btn">{t("nav.openPortal")}</Button></Link>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <Link to="/login"><Button variant="outline" className="w-full" data-testid="mobile-login-btn">{t("nav.signIn")}</Button></Link>
                <Link to="/register"><Button className="w-full" data-testid="mobile-register-btn">{t("nav.getStarted")}</Button></Link>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
